// locales.js - All UI strings for i18n
// Keys follow pattern: screen.element or feature.message

export const locales = {
  en: {
    // App name
    'app.name': 'ScanToDesk',
    'app.tagline': 'Phone Scan → PC Transfer',

    // Landing screen
    'landing.title': 'ScanToDesk',
    'landing.subtitle': 'Phone Scan → PC Transfer',
    'landing.headline': 'Scan documents with your phone',
    'landing.description': 'Scan a QR code to connect your phone and PC.\nCapture a document and it transfers to your PC instantly.',
    'landing.howToUse': 'How to use',
    'landing.step1': 'Open ScanToDesk app on your PC',
    'landing.step2': 'Scan the QR code on screen with your phone',
    'landing.step3': 'Capture a document and it transfers to your PC',
    'landing.download': 'Download PC App',
    'landing.downloadNote': 'This page opens when you scan the QR code displayed on your PC.',
    'landing.startButton': 'Start Scanning',

    // Pairing screen
    'pairing.title': 'ScanToDesk',
    'pairing.subtitle': 'Connect Phone',
    'pairing.instruction': 'Scan the QR code shown on your PC',

    // Unsupported screen
    'unsupported.title': "This device isn't supported",
    'unsupported.message': 'Please scan the QR code\nwith a phone that has a camera.',

    // Camera screen
    'camera.connected': '{name} Connected',
    'camera.connectedDefault': 'ScanToDesk PC Connected',
    'camera.checking': 'Checking PC...',
    'camera.reconnecting': 'Reconnecting to PC...',
    'camera.connecting': 'Connecting to {name}...',
    'camera.connectingDefault': 'Connecting to PC...',
    'camera.disconnected': 'PC Disconnected',
    'camera.recovering': 'Checking connection...',
    'camera.recoveringHint': 'Please wait a moment',
    'camera.queuePending': 'Pending: {count} photos',
    'camera.guideSimple': 'Align the document within the frame',
    'camera.guideDocument': 'Auto-capture when document is detected',
    'camera.modeSimple': 'Photo',
    'camera.modeDocument': 'Document Scan',
    'camera.capture': 'Capture',

    // Camera errors
    'camera.error.notAllowed': 'Camera permission denied. Please allow camera access in settings.',
    'camera.error.notFound': 'Camera not found.',
    'camera.error.generic': 'Camera initialization failed: {message}',
    'camera.error.notInitialized': 'Camera is not initialized.',
    'camera.error.streamNotReady': 'Video stream is not ready.',

    // Camera permission screen
    'cameraPermission.title': 'Camera access needed',
    'cameraPermission.description': 'Please allow camera access to scan documents',
    'cameraPermission.safariGuide.title': 'How to allow in Safari:',
    'cameraPermission.safariGuide.step1': 'Tap <strong>ᴬA</strong> button (left of address bar)',
    'cameraPermission.safariGuide.step2': 'Select <strong>Website Settings</strong>',
    'cameraPermission.safariGuide.step3': 'Camera → <strong>Allow</strong>',
    'cameraPermission.chromeGuide.title': 'How to allow in Chrome:',
    'cameraPermission.chromeGuide.step1': 'Tap the <strong>lock</strong> icon (left of address bar)',
    'cameraPermission.chromeGuide.step2': 'Select <strong>Permissions</strong> or <strong>Site settings</strong>',
    'cameraPermission.chromeGuide.step3': 'Camera → <strong>Allow</strong>',
    'cameraPermission.retry': 'Done, try again',
    'cameraPermission.hint': 'After changing settings, tap the button above',

    // Realtime detection status
    'detect.searching': 'Looking for document',
    'detect.unstable': 'Hold the document steady',
    'detect.stabilizing': 'Hold still...',
    'detect.capturing': 'Capturing...',
    'detect.failed': 'Detection failed. Please capture manually.',

    // Preview screen
    'preview.title': 'Preview',
    'preview.imageAlt': 'Captured image',
    'preview.retake': 'Retake',
    'preview.send': 'Send',

    // Uploading screen
    'upload.converting': 'Converting to PDF...',
    'upload.sending': 'Sending to PC...',

    // Complete screen
    'complete.title': 'Sent!',
    'complete.message': 'Document sent to PC',
    'complete.newScan': 'Scan Another',

    // Settings screen
    'settings.title': 'Settings',
    'settings.connectionStatus': 'Connection Status',
    'settings.connected': 'Connected',
    'settings.notConnected': 'Not Connected',
    'settings.paperSize': 'Paper Size',
    'settings.paperSizeDesc': 'Paper size for PDF output',
    'settings.engine': 'Detection Engine',
    'settings.engineDesc': 'Try WASM if performance is slow',
    'settings.engineAuto': 'Auto (GPU)',
    'settings.engineWasm': 'WASM',
    'settings.inferenceTime': 'Avg inference: {time}ms ({count} runs)',
    'settings.inferenceTimeEmpty': 'Avg inference: --ms',
    'settings.report': 'Report Issue',
    'settings.reportDesc': 'Having trouble? Let us know',
    'settings.reportButton': 'Report Issue',
    'settings.disconnect': 'Disconnect',
    'settings.disconnectDesc': 'Disconnect from PC and scan a new QR code',
    'settings.disconnectConfirm': 'Disconnect from PC?',
    'settings.language': 'Language',
    'settings.version': 'v{version}',

    // Corner edit screen
    'corner.title': 'Verify Document Area',
    'corner.instruction': 'Drag the blue dots to match document corners',
    'corner.retake': 'Retake',
    'corner.confirm': 'Confirm',
    'corner.rotateLeft': 'Rotate left',
    'corner.rotateRight': 'Rotate right',

    // Filter select screen
    'filter.title': 'Select Style',
    'filter.bw': 'B&W',
    'filter.gray': 'Gray',
    'filter.color': 'Color',
    'filter.processing': 'Processing image...',
    'filter.retake': 'Retake',
    'filter.confirm': 'Confirm',
    'filter.sizeLabel': '{filter} · {size}',

    // Process options screen (deprecated)
    'process.title': 'Select Scan Style',
    'process.instruction': 'Choose your preferred style',
    'process.bw': 'Black & White',
    'process.gray': 'Grayscale',
    'process.color': 'Color',
    'process.bwDesc': 'Best for text documents',
    'process.grayDesc': 'Good for diagrams',
    'process.colorDesc': 'Best for color documents, photos',
    'process.calculating': 'Calculating...',
    'process.retake': 'Retake',

    // Multipage screen
    'multipage.title': 'Scanned Documents',
    'multipage.count': '{count} pages',
    'multipage.countSingle': '1 page',
    'multipage.send': 'Send',
    'multipage.addPage': '+ Add Page',
    'multipage.maxPages': 'Maximum {max} pages allowed',

    // Error screen
    'error.title': 'Error',
    'error.message': 'Something went wrong',
    'error.retry': 'Try Again',
    'error.noImage': 'No captured image.',

    // Disconnect dialog
    'disconnect.title': 'Connection Lost',
    'disconnect.message': 'Lost connection to PC.\nRestart ScanToDesk on your PC\nand scan the QR code again.',
    'disconnect.reconnect': 'Reconnect',

    // Disconnect reasons
    'disconnect.noPairing.title': 'No Connection Info',
    'disconnect.noPairing.message': 'Not connected to PC.\nPlease scan the QR code.',
    'disconnect.unauthorized.title': 'Connection Lost',
    'disconnect.unauthorized.message': 'Lost connection to PC.\nRestart ScanToDesk on your PC\nand scan the QR code again.',
    'disconnect.sessionNotFound.title': 'Connection Lost',
    'disconnect.sessionNotFound.message': 'Lost connection to PC.\nRestart ScanToDesk on your PC\nand scan the QR code again.',
    'disconnect.sessionExpired.title': 'Session Expired',
    'disconnect.sessionExpired.message': 'Your session has expired.\nRestart ScanToDesk on your PC\nand scan the QR code again.',
    'session.switched': 'Switched to new session',
    'disconnect.networkError.title': 'Check Internet Connection',
    'disconnect.networkError.message': 'Please check your internet connection.\nTry again when connected.',
    'disconnect.unknownError.title': 'Connection Error',
    'disconnect.unknownError.message': 'Something went wrong.\nRestart ScanToDesk on your PC\nand scan the QR code again.',

    // Restore dialog
    'restore.title': 'Previous photo found',
    'restore.message': 'Use this photo?',
    'restore.discard': 'Take New',
    'restore.use': 'Use This',
    'restore.imageAlt': 'Saved photo',

    // OpenCV loading dialog
    'opencv.title': 'Preparing scan feature...',
    'opencv.message': "One-time download.\nIt'll work instantly next time.",

    // Mode switch dialog
    'modeSwitch.title': 'Document in progress',
    'modeSwitch.message': 'Switching modes will discard collected pages.\nSwitch anyway?',
    'modeSwitch.cancel': 'Cancel',
    'modeSwitch.confirm': 'Switch',

    // Other tab dialog
    'otherTab.title': 'Already open in another tab',
    'otherTab.message': 'ScanToDesk is open in another tab.\nPlease continue using that tab.',
    'otherTab.goToTab': 'Go to Other Tab',
    'otherTab.hint': "If you can't switch, find the other tab manually",

    // Tab redirect screen
    'redirect.title': 'Switched to your other tab',
    'redirect.message': 'Your existing ScanToDesk tab is now connected to the new QR code.',
    'redirect.closeHint': 'You can close this tab.',

    // Toast messages
    'toast.autoSent': '{count} photos auto-sent!',
    'toast.processingFailed': 'Processing failed, saved as original',
    'toast.detectionFailed': 'Detection issue. Please capture manually.',
    'toast.captureFailed': 'Capture failed. Please try again.',
    'toast.cornerDetectionFailed': "Couldn't detect document. Please adjust manually.",
    'toast.engineChanged.wasm': 'Switched to WASM engine',
    'toast.engineChanged.gpu': 'Switched to GPU engine',
    'toast.engineChangeFailed': 'Engine switch failed',
    'toast.engineChanging': 'Switching engine...',

    // Upload limits
    'upload.remaining': '{remaining}/{limit}',
    'upload.remainingToday': 'Remaining today: {remaining}/{limit}',
    'upload.lastToday': "That was today's last free transfer",
    'upload.limitReached.title': "Today's limit reached",
    'upload.limitReached.message': 'Upgrade to Pro on your PC\nfor unlimited transfers.',
    'upload.limitReached.ok': 'Got it',
    'upload.upgradeHint': 'Upgrade to Pro on your PC\nfor unlimited transfers',

    // Upload errors
    'upload.error.limit': "You've reached today's upload limit ({limit} photos).\nUpgrade to Pro for unlimited transfers.",
    'upload.error.offline': "Internet connection unstable.\nDon't worry, your photo is saved.\nIt will auto-send when connected.",
    'upload.error.sessionLost': 'Connection lost.\nPlease reconnect.',
    'upload.error.failed': "Photo saved.\nIt will auto-send when connected.",
    'upload.error.saveFailed': 'Failed to save photo.',

    // Pairing confirmation
    'pairing.confirmSwitch': 'You have {count} unsent photo(s). Switching to a new computer will remove them. Continue?',

    // Pairing errors
    'pairing.error.cameraNotAllowed': 'Cannot access camera. Please check permissions.',
    'pairing.error.codeExpired': 'This QR code is no longer active.\nPlease check your computer for a new one.',
    'pairing.error.codeNotFound': 'QR code not recognized.\nPlease try scanning again.',
    'pairing.error.codeInvalid': "This QR code can't be used.\nPlease check your computer for a new one.",
    'pairing.error.connectionFailed': "Couldn't connect.\nPlease check your internet and try again.",
    'pairing.error.saveFailed': 'Failed to save pairing info.',
    'pairing.error.invalidQr': 'Invalid QR code.',
    'pairing.error.noData': 'No pairing info in QR code.',

    // Link sharing errors
    'link.error.expired': 'This sharing link has expired.',
    'link.error.notFound': 'Invalid sharing link.',
    'link.error.failed': 'Failed to load link info.',

    // Link confirm screen
    'link.confirm.title': 'Send Documents',
    'link.confirm.description': 'Scanned documents will be sent directly to the PC',
    'link.confirm.button': 'Start Scanning',
    'link.confirm.connecting': 'Connecting...',
    'link.confirm.pcOffline': 'PC may be offline. Documents will be sent when PC is online.',

    // Link complete screen
    'link.complete.sendAnother': 'Send Another',
    'link.complete.done': 'Done',

    // Model loading
    'model.loading.recognition': 'Preparing document recognition...',
    'model.loading.scan': 'Preparing scan engine...',
    'model.loading.failed.title': 'Scan Preparation Failed',
    'model.loading.failed.message': "Couldn't prepare document recognition.\nCheck your internet and try again.",

    // Error report
    'error.report': 'Report Issue',
    'report.title': 'Report Issue',
    'report.message': 'Send error info to the developer?',
    'report.privacy': "Don't worry, no personal info is included",
    'report.submit': 'Send',
    'report.cancel': 'Cancel',
    'report.sending': 'Sending...',
    'report.success.title': 'Thank you',
    'report.success.message': 'Our team will review it',
    'report.success.ok': 'OK',
  },

  ko: {
    // App name
    'app.name': 'ScanToDesk',
    'app.tagline': '스마트폰 스캔 → PC 전송',

    // Landing screen
    'landing.title': 'ScanToDesk',
    'landing.subtitle': '스마트폰 스캔 → PC 전송',
    'landing.headline': '스마트폰 카메라로 문서 스캔',
    'landing.description': 'QR 코드를 스캔하면 스마트폰과 PC가 연결됩니다.\n문서를 촬영하면 PC에 바로 전송됩니다.',
    'landing.howToUse': '사용 방법',
    'landing.step1': 'PC에서 ScanToDesk 앱을 실행합니다',
    'landing.step2': '화면의 QR 코드를 스마트폰으로 스캔합니다',
    'landing.step3': '문서를 촬영하면 PC에 바로 전송됩니다',
    'landing.download': 'PC 앱 다운로드',
    'landing.downloadNote': '이 페이지는 PC에서 표시된 QR 코드를\n스캔했을 때 열립니다.',
    'landing.startButton': '스캔 시작',

    // Pairing screen
    'pairing.title': 'ScanToDesk',
    'pairing.subtitle': '휴대폰 연결',
    'pairing.instruction': 'PC에 표시된 QR 코드를 스캔하세요',

    // Unsupported screen
    'unsupported.title': '이 기기에서는 사용할 수 없어요',
    'unsupported.message': '카메라가 있는 스마트폰에서\nQR 코드를 스캔해 주세요.',

    // Camera screen
    'camera.connected': '{name} 연결됨',
    'camera.connectedDefault': 'ScanToDesk PC 연결됨',
    'camera.checking': 'PC 확인 중...',
    'camera.reconnecting': 'PC 재연결 중...',
    'camera.connecting': '{name} 연결 중...',
    'camera.connectingDefault': 'PC 연결 중...',
    'camera.disconnected': 'PC 연결 끊김',
    'camera.recovering': '연결 확인 중...',
    'camera.recoveringHint': '잠시만 기다려주세요',
    'camera.queuePending': '대기 중: {count}장',
    'camera.guideSimple': '문서를 프레임 안에 맞추세요',
    'camera.guideDocument': '문서가 감지되면 자동으로 촬영해요',
    'camera.modeSimple': '사진',
    'camera.modeDocument': '문서 스캔',
    'camera.capture': '촬영',

    // Camera errors
    'camera.error.notAllowed': '카메라 권한이 거부되었습니다. 설정에서 권한을 허용해주세요.',
    'camera.error.notFound': '카메라를 찾을 수 없습니다.',
    'camera.error.generic': '카메라 초기화에 실패했습니다: {message}',
    'camera.error.notInitialized': '카메라가 초기화되지 않았습니다.',
    'camera.error.streamNotReady': '비디오 스트림이 준비되지 않았습니다.',

    // Camera permission screen
    'cameraPermission.title': '카메라 권한이 필요해요',
    'cameraPermission.description': '문서를 스캔하려면 카메라 사용을 허용해주세요',
    'cameraPermission.safariGuide.title': 'Safari에서 권한 허용하기:',
    'cameraPermission.safariGuide.step1': '주소창 왼쪽 <strong>ᴬA</strong> 버튼 탭',
    'cameraPermission.safariGuide.step2': '<strong>웹 사이트 설정</strong> 선택',
    'cameraPermission.safariGuide.step3': '카메라 → <strong>허용</strong> 선택',
    'cameraPermission.chromeGuide.title': 'Chrome에서 권한 허용하기:',
    'cameraPermission.chromeGuide.step1': '주소창 왼쪽 <strong>자물쇠</strong> 아이콘 탭',
    'cameraPermission.chromeGuide.step2': '<strong>권한</strong> 또는 <strong>사이트 설정</strong> 선택',
    'cameraPermission.chromeGuide.step3': '카메라 → <strong>허용</strong> 선택',
    'cameraPermission.retry': '설정 완료, 다시 시도',
    'cameraPermission.hint': '설정을 변경한 후 위 버튼을 눌러주세요',

    // Realtime detection status
    'detect.searching': '문서를 찾고 있어요',
    'detect.unstable': '문서를 안정적으로 잡아주세요',
    'detect.stabilizing': '잠시만 유지해주세요...',
    'detect.capturing': '촬영 중...',
    'detect.failed': '실시간 감지에 문제가 생겼어요. 수동으로 촬영해주세요.',

    // Preview screen
    'preview.title': '미리보기',
    'preview.imageAlt': '촬영된 이미지',
    'preview.retake': '다시 찍기',
    'preview.send': '전송',

    // Uploading screen
    'upload.converting': 'PDF 변환 중...',
    'upload.sending': '전송 중...',

    // Complete screen
    'complete.title': '전송 완료!',
    'complete.message': '문서가 PC로 전송되었어요',
    'complete.newScan': '새로 촬영하기',

    // Settings screen
    'settings.title': '설정',
    'settings.connectionStatus': '연결 상태',
    'settings.connected': '연결됨',
    'settings.notConnected': '연결 안됨',
    'settings.paperSize': '용지 크기',
    'settings.paperSizeDesc': 'PDF를 만들 때 사용할 용지 크기예요',
    'settings.engine': '문서 감지 엔진',
    'settings.engineDesc': '느리면 WASM으로 변경해보세요',
    'settings.engineAuto': '자동 (GPU)',
    'settings.engineWasm': 'WASM',
    'settings.inferenceTime': '평균 추론 시간: {time}ms ({count}회)',
    'settings.inferenceTimeEmpty': '평균 추론 시간: --ms',
    'settings.report': '문제 신고',
    'settings.reportDesc': '앱에 문제가 있나요? 개발팀에 알려주세요',
    'settings.reportButton': '문제 신고하기',
    'settings.disconnect': '연결 종료',
    'settings.disconnectDesc': 'PC와의 연결을 끊고 새로운 QR 코드를 스캔합니다',
    'settings.disconnectConfirm': '페어링을 재설정하시겠습니까?',
    'settings.language': '언어 / Language',
    'settings.version': 'v{version}',

    // Corner edit screen
    'corner.title': '문서 영역 확인',
    'corner.instruction': '파란 점을 끌어서 문서 모서리를 맞추세요',
    'corner.retake': '다시 찍기',
    'corner.confirm': '확인',
    'corner.rotateLeft': '반시계방향 회전',
    'corner.rotateRight': '시계방향 회전',

    // Filter select screen
    'filter.title': '스캔 스타일 선택',
    'filter.bw': '흑백',
    'filter.gray': '그레이',
    'filter.color': '컬러',
    'filter.processing': '이미지 처리 중...',
    'filter.retake': '다시 찍기',
    'filter.confirm': '확인',
    'filter.sizeLabel': '{filter} · {size}',

    // Process options screen (deprecated)
    'process.title': '스캔 스타일 선택',
    'process.instruction': '원하는 스타일을 선택하세요',
    'process.bw': '흑백',
    'process.gray': '그레이스케일',
    'process.color': '컬러',
    'process.bwDesc': '텍스트 문서에 최적',
    'process.grayDesc': '도면, 다이어그램에 좋음',
    'process.colorDesc': '컬러 문서, 사진에 최적',
    'process.calculating': '계산 중...',
    'process.retake': '다시 찍기',

    // Multipage screen
    'multipage.title': '스캔한 문서',
    'multipage.count': '{count}페이지',
    'multipage.countSingle': '1페이지',
    'multipage.send': '전송',
    'multipage.addPage': '+ 페이지 추가',
    'multipage.maxPages': '최대 {max}장까지 추가할 수 있어요',

    // Error screen
    'error.title': '오류 발생',
    'error.message': '문제가 발생했습니다',
    'error.retry': '다시 시도',
    'error.noImage': '촬영된 이미지가 없습니다.',

    // Disconnect dialog
    'disconnect.title': '연결이 끊어졌어요',
    'disconnect.message': 'PC와 연결이 끊어졌어요.\nPC에서 ScanToDesk을 다시 실행하고\nQR 코드를 스캔해주세요.',
    'disconnect.reconnect': '다시 연결하기',

    // Disconnect reasons
    'disconnect.noPairing.title': '연결 정보 없음',
    'disconnect.noPairing.message': 'PC와 연결되어 있지 않아요.\nQR 코드를 스캔해주세요.',
    'disconnect.unauthorized.title': '연결이 끊어졌어요',
    'disconnect.unauthorized.message': 'PC와 연결이 끊어졌어요.\nPC에서 ScanToDesk를 다시 실행하고\nQR 코드를 스캔해주세요.',
    'disconnect.sessionNotFound.title': '연결이 끊어졌어요',
    'disconnect.sessionNotFound.message': 'PC와 연결이 끊어졌어요.\nPC에서 ScanToDesk를 다시 실행하고\nQR 코드를 스캔해주세요.',
    'disconnect.sessionExpired.title': '연결 시간이 지났어요',
    'disconnect.sessionExpired.message': '연결 시간이 지났어요.\nPC에서 ScanToDesk를 다시 실행하고\nQR 코드를 스캔해주세요.',
    'session.switched': '새 세션으로 전환되었어요',
    'disconnect.networkError.title': '인터넷 연결 확인',
    'disconnect.networkError.message': '인터넷 연결을 확인해주세요.\n연결이 되면 다시 시도해주세요.',
    'disconnect.unknownError.title': '연결 오류',
    'disconnect.unknownError.message': '문제가 발생했어요.\nPC에서 ScanToDesk를 다시 실행하고\nQR 코드를 스캔해주세요.',

    // Restore dialog
    'restore.title': '아까 찍은 사진이 있어요',
    'restore.message': '이 사진을 사용할까요?',
    'restore.discard': '새로 찍기',
    'restore.use': '사용하기',
    'restore.imageAlt': '저장된 사진',

    // OpenCV loading dialog
    'opencv.title': '스캔 기능 준비 중...',
    'opencv.message': '처음 한 번만 다운로드하면\n다음부터 바로 사용할 수 있어요',

    // Mode switch dialog
    'modeSwitch.title': '스캔 중인 문서가 있어요',
    'modeSwitch.message': '모드를 변경하면 수집 중인 페이지가 사라져요.\n정말 변경할까요?',
    'modeSwitch.cancel': '취소',
    'modeSwitch.confirm': '변경하기',

    // Other tab dialog
    'otherTab.title': '이미 다른 탭에서 사용 중이에요',
    'otherTab.message': 'ScanToDesk이 다른 탭에서 열려있어요.\n기존 탭에서 계속 사용해주세요.',
    'otherTab.goToTab': '기존 탭으로 이동',
    'otherTab.hint': '이동이 안 되면 다른 탭을 직접 찾아주세요',

    // Tab redirect screen
    'redirect.title': '기존 탭으로 전환했어요',
    'redirect.message': '기존 ScanToDesk 탭이 새 QR 코드에 연결되었어요.',
    'redirect.closeHint': '이 탭은 닫아도 돼요.',

    // Toast messages
    'toast.autoSent': '{count}장 자동 전송 완료!',
    'toast.processingFailed': '이미지 처리에 실패하여 원본으로 저장했어요',
    'toast.detectionFailed': '실시간 감지에 문제가 생겼어요. 수동으로 촬영해주세요.',
    'toast.captureFailed': '촬영에 실패했어요. 다시 시도해주세요.',
    'toast.cornerDetectionFailed': '문서를 자동으로 찾지 못했어요. 직접 조절해주세요.',
    'toast.engineChanged.wasm': 'WASM 엔진으로 변경됨',
    'toast.engineChanged.gpu': 'GPU 엔진으로 변경됨',
    'toast.engineChangeFailed': '엔진 변경 실패',
    'toast.engineChanging': '엔진 변경 중...',

    // Upload limits
    'upload.remaining': '{remaining}/{limit}',
    'upload.remainingToday': '오늘 남은 전송: {remaining}/{limit}',
    'upload.lastToday': '오늘 마지막 무료 전송이었어요',
    'upload.limitReached.title': '오늘 전송 한도 도달',
    'upload.limitReached.message': 'PC에서 Pro로 업그레이드하면\n무제한으로 전송할 수 있어요.',
    'upload.limitReached.ok': '확인',
    'upload.upgradeHint': 'PC에서 Pro로 업그레이드하면\n무제한으로 전송할 수 있어요',

    // Upload errors
    'upload.error.limit': '오늘 업로드 한도({limit}장)에 도달했습니다.\nPro로 업그레이드하면 무제한으로 전송할 수 있어요.',
    'upload.error.offline': '인터넷 연결이 불안정해요.\n사진은 저장되어 있으니 걱정 마세요.\n연결이 되면 자동으로 전송됩니다.',
    'upload.error.sessionLost': '연결 끊김\n다시 페어링해주세요.',
    'upload.error.failed': '사진이 저장되었어요.\n인터넷 연결이 되면 자동으로 전송됩니다.',
    'upload.error.saveFailed': '사진을 저장하는데 실패했어요.',

    // Pairing confirmation
    'pairing.confirmSwitch': '보내지 않은 사진이 {count}장 있습니다. 새 컴퓨터로 전환하면 삭제됩니다. 계속하시겠습니까?',

    // Pairing errors
    'pairing.error.cameraNotAllowed': '카메라를 사용할 수 없습니다. 권한을 확인해주세요.',
    'pairing.error.codeExpired': '이 QR 코드는 더 이상 유효하지 않아요.\n컴퓨터에서 새 QR 코드를 확인해 주세요.',
    'pairing.error.codeNotFound': 'QR 코드를 인식할 수 없어요.\n다시 스캔해 주세요.',
    'pairing.error.codeInvalid': '이 QR 코드는 사용할 수 없어요.\n컴퓨터에서 새 QR 코드를 확인해 주세요.',
    'pairing.error.connectionFailed': '연결할 수 없어요.\n인터넷 연결을 확인하고 다시 시도해 주세요.',
    'pairing.error.saveFailed': '페어링 정보 저장에 실패했습니다.',
    'pairing.error.invalidQr': '유효하지 않은 QR 코드입니다.',
    'pairing.error.noData': 'QR 코드에 페어링 정보가 없습니다.',

    // Link sharing errors
    'link.error.expired': '이 공유 링크가 만료되었습니다.',
    'link.error.notFound': '유효하지 않은 공유 링크입니다.',
    'link.error.failed': '링크 정보를 불러오는데 실패했습니다.',

    // Link confirm screen
    'link.confirm.title': '문서 보내기',
    'link.confirm.description': '스캔한 문서가 PC로 바로 전송됩니다',
    'link.confirm.button': '스캔 시작',
    'link.confirm.connecting': '연결 중...',
    'link.confirm.pcOffline': 'PC가 오프라인일 수 있습니다. PC가 온라인이 되면 전송됩니다.',

    // Link complete screen
    'link.complete.sendAnother': '추가로 보내기',
    'link.complete.done': '완료',

    // Model loading
    'model.loading.recognition': '문서 인식 준비 중...',
    'model.loading.scan': '스캔 엔진 준비 중...',
    'model.loading.failed.title': '스캔 준비 실패',
    'model.loading.failed.message': '문서 인식 기능을 준비하지 못했어요.\n인터넷 연결을 확인하고 다시 시도해주세요.',

    // Error report
    'error.report': '문제 신고',
    'report.title': '문제 신고',
    'report.message': '개발팀에 오류 정보를 보낼까요?',
    'report.privacy': '걱정 마세요, 개인정보는 포함되지 않아요',
    'report.submit': '보내기',
    'report.cancel': '취소',
    'report.sending': '전송 중...',
    'report.success.title': '감사합니다',
    'report.success.message': '개발팀이 검토할 예정이에요',
    'report.success.ok': '확인',
  }
};
